import { useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { apiUrl } from '../../utils/api';

// Wird beim Build durch die Kennung ersetzt (build.ts, define); im Dev-Server nicht gesetzt.
declare const __BUILD_ID__: string | undefined;
const CURRENT = typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : null;
const CHECK_EVERY_MS = 2 * 60 * 1000;

/**
 * Merkt, wenn das Panel neu ausgerollt wurde (dist/version.json ≠ eingebaute Kennung),
 * und bietet „Neu laden" an – offene Tabs liefen sonst mit altem Code weiter.
 * Bewusst kein automatisches Neuladen: ungespeicherte Eingaben sollen nicht verloren gehen.
 */
export const UpdateBanner = () => {
  const [outdated, setOutdated] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!CURRENT) return;
    let stopped = false;
    const check = async () => {
      if (stopped || document.hidden) return;
      try {
        const res = await fetch(apiUrl(`/version.json?t=${Date.now()}`), { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (data?.build && data.build !== CURRENT) setOutdated(true);
      } catch {
        /* Panel startet gerade neu oder offline – beim nächsten Mal wieder */
      }
    };
    const onVisible = () => { if (!document.hidden) check(); };
    const first = setTimeout(check, 15000);
    const timer = setInterval(check, CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);
    return () => {
      stopped = true;
      clearTimeout(first);
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
    };
  }, []);

  if (!outdated || dismissed) return null;

  return (
    <div
      role="status"
      className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-3 max-w-[calc(100vw-2rem)] rounded-xl border border-ember/40 bg-elevated/95 backdrop-blur px-4 py-3 shadow-2xl shadow-black/60 animate-in slide-in-from-bottom-5"
    >
      <RefreshCw size={16} className="shrink-0 text-ember" />
      <span className="text-sm text-fg">Das Panel wurde aktualisiert – bitte einmal neu laden.</span>
      <button
        onClick={() => window.location.reload()}
        className="shrink-0 rounded-lg bg-ember px-3 py-1.5 text-xs font-semibold text-ink hover:bg-ember-2 transition-colors"
      >
        Jetzt neu laden
      </button>
      <button onClick={() => setDismissed(true)} aria-label="Später" title="Später" className="shrink-0 p-1 text-fg-muted hover:text-fg">
        <X size={15} />
      </button>
    </div>
  );
};
